import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

const CLOUDINARY_IMAGE_UPLOAD_MARKER = '/image/upload/';
const CLOUDINARY_IMAGE_LIST_BASE_URL = 'https://media.bkstr.com/image/list/';
const ENHANCED_PUBLIC_ID_SUFFIXES = ['E01', 'E02', 'E03', 'E04'];
const DEFAULT_ENHANCED_IMAGE_WIDTHS = [240, 352, 832, 1200, 1600, 1920, 2560, 3840];
const ENHANCED_ASSETS_TIMEOUT_MS = 1500;

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });

    this.#applyEnhancedPdpAssets(signal);
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    this.replaceWith(newMediaGallery);
  };

  /**
   * Checks Cloudinary for ClaidAI-enhanced PDP assets and swaps them in before the gallery is revealed.
   * If no enhanced assets exist, the server-rendered Shopify media remains intact.
   *
   * @param {AbortSignal} signal
   */
  async #applyEnhancedPdpAssets(signal) {
    if (this.dataset.enhancedPdpAssets !== 'true') {
      this.#revealEnhancedPdpAssets();
      return;
    }

    let enhancedAssetsApplied = false;

    try {
      const sourceImage = this.querySelector('img.product-media__image');
      const sourceUrl = sourceImage instanceof HTMLImageElement ? sourceImage.currentSrc || sourceImage.src : '';
      const sourcePublicId = getCloudinaryPublicId(sourceUrl);
      const tag = getCloudinaryEnhancedTag(sourcePublicId);

      logEnhancedPdpAssets('checking', { sourceUrl, sourcePublicId, tag });

      if (!tag) {
        logEnhancedPdpAssets('skipping: unable to resolve tag');
        return;
      }

      const publicIds = await withTimeout(fetchEnhancedPublicIds(tag, signal), ENHANCED_ASSETS_TIMEOUT_MS);
      if (signal.aborted) return;
      if (!publicIds.length) {
        logEnhancedPdpAssets('fallback: no enhanced assets found', { tag });
        return;
      }

      logEnhancedPdpAssets('applying enhanced assets', { tag, publicIds });
      await this.#renderEnhancedPdpAssets(publicIds);
      enhancedAssetsApplied = true;
    } catch (error) {
      logEnhancedPdpAssets('fallback: request failed or timed out', error);
      // Keep the default gallery whenever enhanced assets are unavailable or slow.
    } finally {
      if (!signal.aborted) {
        if (!enhancedAssetsApplied) this.#removeEnhancedPdpPlaceholders();
        logEnhancedPdpAssets('revealing gallery');
        this.#revealEnhancedPdpAssets();
      }
    }
  }

  #revealEnhancedPdpAssets() {
    this.style.removeProperty('visibility');
    this.classList.remove('media-gallery--enhanced-checking');
  }

  #removeEnhancedPdpPlaceholders() {
    this.querySelectorAll('.enhanced-pdp-assets__placeholder-controls, .enhanced-pdp-assets__placeholder-arrows').forEach(
      (element) => element.remove()
    );

    const slideshow = this.querySelector('slideshow-component');
    const slideCount = this.querySelectorAll('slideshow-slide').length;
    if (slideshow && slideCount <= 1) {
      slideshow.classList.add('product-media-gallery__slideshow--single-media', 'slideshow--single-media');
    }
  }

  /**
   * @param {string[]} publicIds
   */
  async #renderEnhancedPdpAssets(publicIds) {
    const imageUrls = publicIds.map((publicId) => ({
      publicId,
      url: this.#buildEnhancedImageUrl(publicId),
    }));

    this.#syncCarouselSlides(imageUrls);
    this.#syncGridItems(imageUrls);
    this.#syncSlideshowThumbnails(imageUrls);
    this.#syncZoomDialog(imageUrls);
    this.slideshow?.updatedCallback?.();
    this.refs.zoomDialogComponent?.updatedCallback?.();
    this.slideshow?.select?.(0, undefined, { animate: false });
    await waitForEnhancedMainImage(this);
  }

  /**
   * @param {string} publicId
   * @param {number} [width]
   */
  #buildEnhancedImageUrl(publicId, width) {
    const prefix = (this.dataset.cloudinaryImagePrefix || 'https://media.bkstr.com/image/upload').replace(/\/$/, '');
    const transformation = normalizeCloudinaryTransformation(this.dataset.cloudinaryImageTransformation || '/f_auto,q_auto');
    const widthTransformation = width ? `/c_limit,w_${width}` : '';

    return `${prefix}${transformation}${widthTransformation}/${publicId}`;
  }

  /**
   * @param {{ publicId: string; url: string }[]} imageUrls
   */
  #syncCarouselSlides(imageUrls) {
    const scroller = this.querySelector('slideshow-slides');
    if (!scroller) return;

    const slides = syncElementCount(scroller, 'slideshow-slide', imageUrls.length);
    slides.forEach((slide, index) => {
      const imageUrl = imageUrls[index];
      if (!imageUrl) return;

      slide.setAttribute('aria-hidden', index === 0 ? 'false' : 'true');
      slide.style.setProperty('view-timeline-name', `--slide-${index}`);
      updateIndexedAction(slide, 'on:click', index);
      replaceImages(slide, imageUrl.publicId, (width) => this.#buildEnhancedImageUrl(imageUrl.publicId, width));
    });
  }

  /**
   * @param {{ publicId: string; url: string }[]} imageUrls
   */
  #syncGridItems(imageUrls) {
    const grid = this.querySelector('.media-gallery__grid');
    if (!grid) return;

    const items = syncElementCount(grid, 'li.product-media-container', imageUrls.length);
    items.forEach((item, index) => {
      const imageUrl = imageUrls[index];
      if (!imageUrl) return;

      item.setAttribute('ref', 'media[]');
      replaceImages(item, imageUrl.publicId, (width) => this.#buildEnhancedImageUrl(imageUrl.publicId, width));
    });
  }

  /**
   * @param {{ publicId: string; url: string }[]} imageUrls
   */
  #syncSlideshowThumbnails(imageUrls) {
    const thumbnailLists = this.querySelectorAll('.slideshow-controls__thumbnails');

    thumbnailLists.forEach((thumbnailList) => {
      const thumbnails = syncElementCount(thumbnailList, 'button.slideshow-controls__thumbnail', imageUrls.length);
      thumbnails.forEach((thumbnail, index) => {
        const imageUrl = imageUrls[index];
        if (!imageUrl) return;

        thumbnail.setAttribute('aria-selected', index === 0 ? 'true' : 'false');
        thumbnail.setAttribute('on:click', `/select/${index}`);
        replaceImages(thumbnail, imageUrl.publicId, (width) => this.#buildEnhancedImageUrl(imageUrl.publicId, width || 144));
      });
    });

    this.querySelectorAll('.slideshow-controls__counter').forEach((counter) => {
      const current = counter.querySelector('[ref="current"]');
      if (current) current.textContent = '1';
      const slash = counter.querySelector('.slash');
      if (slash?.nextSibling) slash.nextSibling.textContent = String(imageUrls.length);
    });

    this.querySelectorAll('.slideshow-controls__dots').forEach((dots) => {
      const items = syncElementCount(dots, 'li', imageUrls.length);
      items.forEach((item, index) => {
        const button = item.querySelector('button');
        if (!button) return;

        button.classList.toggle('is-selected', index === 0);
        button.setAttribute('aria-label', `Slide ${index + 1} of ${imageUrls.length}`);
        button.setAttribute('on:click', `/select/${index}`);
        button.textContent = String(index + 1);
      });
    });
  }

  /**
   * @param {{ publicId: string; url: string }[]} imageUrls
   */
  #syncZoomDialog(imageUrls) {
    const thumbnailList = this.querySelector('.dialog-thumbnails-list');
    const zoomGallery = this.querySelector('.dialog-zoomed-gallery');

    if (thumbnailList) {
      const thumbnails = syncElementCount(thumbnailList, 'button.dialog-thumbnails-list__thumbnail', imageUrls.length);
      thumbnails.forEach((thumbnail, index) => {
        const imageUrl = imageUrls[index];
        if (!imageUrl) return;

        thumbnail.setAttribute('aria-selected', index === 0 ? 'true' : 'false');
        thumbnail.setAttribute('on:click', `/handleThumbnailClick/${index}`);
        thumbnail.setAttribute('on:pointerenter', `/handleThumbnailPointerEnter/${index}`);
        replaceImages(thumbnail, imageUrl.publicId, (width) => this.#buildEnhancedImageUrl(imageUrl.publicId, width || 1024));
      });
    }

    if (zoomGallery) {
      const items = syncElementCount(zoomGallery, 'li.product-media-container', imageUrls.length);
      items.forEach((item, index) => {
        const imageUrl = imageUrls[index];
        if (!imageUrl) return;

        item.id = `product-enhanced-${imageUrl.publicId}-${index + 1}`;
        item.setAttribute('ref', 'media[]');
        replaceImages(item, imageUrl.publicId, (width) => this.#buildEnhancedImageUrl(imageUrl.publicId, width));
      });
    }
  }

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Zooms the media gallery at the current slideshow index.
   * Used by the zoom-in control button.
   *
   * @param {PointerEvent} event - The pointer event.
   */
  zoomCurrent(event) {
    const index = this.refs.slideshow?.current ?? 0;
    this.refs.zoomDialogComponent?.open(index, event);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

/**
 * @param {string} url
 */
function getCloudinaryPublicId(url) {
  if (!url) return '';

  try {
    const parsedUrl = new URL(url, window.location.origin);
    const path = decodeURIComponent(parsedUrl.pathname);
    const uploadIndex = path.indexOf(CLOUDINARY_IMAGE_UPLOAD_MARKER);
    const relevantPath = uploadIndex >= 0 ? path.slice(uploadIndex + CLOUDINARY_IMAGE_UPLOAD_MARKER.length) : path;
    const segments = relevantPath.split('/').filter(Boolean);
    const filename = segments.at(-1) || '';

    return stripFileExtension(filename);
  } catch (_error) {
    return '';
  }
}

/**
 * @param {string} publicId
 */
function getCloudinaryEnhancedTag(publicId) {
  if (!publicId || /_E\d+$/i.test(publicId)) return '';

  const publicIdWithoutShopifyHash = publicId.replace(/_[a-z0-9]{6,}$/i, '');

  return publicIdWithoutShopifyHash.replace(/_\d{2,}$/i, '');
}

/**
 * @param {string} tag
 * @param {AbortSignal} signal
 */
async function fetchEnhancedPublicIds(tag, signal) {
  const listUrl = `${CLOUDINARY_IMAGE_LIST_BASE_URL}${encodeURIComponent(tag)}.json`;
  logEnhancedPdpAssets('fetching list endpoint', listUrl);
  const response = await fetch(listUrl, { signal });

  logEnhancedPdpAssets('list endpoint response', { status: response.status, ok: response.ok });

  if (response.status === 404) {
    logEnhancedPdpAssets('404: enhanced assets not found', { tag });
    return [];
  }
  if (!response.ok) throw new Error(`Cloudinary list request failed: ${response.status}`);

  const data = await response.json();
  const resources = Array.isArray(data)
    ? data
    : Array.isArray(data.resources)
      ? data.resources
      : Array.isArray(data.public_ids)
        ? data.public_ids
        : [];
  const publicIdsBySuffix = new Map();

  for (const resource of resources) {
    const publicId = typeof resource === 'string' ? resource : resource?.public_id;
    if (typeof publicId !== 'string') continue;

    const normalizedPublicId = stripFileExtension(publicId);
    const suffix = ENHANCED_PUBLIC_ID_SUFFIXES.find((expectedSuffix) =>
      normalizedPublicId.endsWith(`${tag}_${expectedSuffix}`)
    );

    if (suffix) publicIdsBySuffix.set(suffix, normalizedPublicId);
  }

  if (!publicIdsBySuffix.has('E01')) return [];

  const sortedPublicIds = ENHANCED_PUBLIC_ID_SUFFIXES.map((suffix) => publicIdsBySuffix.get(suffix)).filter(Boolean);
  logEnhancedPdpAssets('sorted enhanced assets', sortedPublicIds);

  return sortedPublicIds;
}

/**
 * @param {Element} parent
 * @param {string} childSelector
 * @param {number} count
 */
function syncElementCount(parent, childSelector, count) {
  let elements = Array.from(parent.querySelectorAll(`:scope > ${childSelector}`));
  const template = elements[0];

  if (!template) return [];

  while (elements.length < count) {
    const clone = /** @type {Element} */ (template.cloneNode(true));
    parent.appendChild(clone);
    elements.push(clone);
  }

  while (elements.length > count) {
    elements.pop()?.remove();
  }

  return Array.from(parent.querySelectorAll(`:scope > ${childSelector}`));
}

/**
 * @param {Element} root
 * @param {string} publicId
 * @param {(width?: number) => string} buildUrl
 */
function replaceImages(root, publicId, buildUrl) {
  root.querySelectorAll('img').forEach((image) => {
    if (!(image instanceof HTMLImageElement)) return;

    const widths = getImageWidths(image);
    const primaryWidth = widths.at(-1);
    const srcset = widths.map((width) => `${buildUrl(width)} ${width}w`).join(', ');

    image.src = buildUrl(primaryWidth);
    image.srcset = srcset;
    image.setAttribute('data_max_resolution', buildUrl(3840));
    image.setAttribute('data-enhanced-public-id', publicId);
  });
}

/**
 * @param {Element} root
 */
async function waitForEnhancedMainImage(root) {
  const image = root.querySelector('slideshow-slide[aria-hidden="false"] img.product-media__image[data-enhanced-public-id]');

  if (!(image instanceof HTMLImageElement)) return;

  logEnhancedPdpAssets('waiting for enhanced main image', image.currentSrc || image.src);
  await withTimeout(waitForImageReady(image), 1200).catch(() => {
    logEnhancedPdpAssets('enhanced main image wait timed out; revealing gallery');
  });
}

/**
 * @param {HTMLImageElement} image
 */
async function waitForImageReady(image) {
  if (image.complete && image.naturalWidth > 0) {
    await image.decode?.().catch(() => {});
    return;
  }

  await new Promise((resolve, reject) => {
    image.addEventListener('load', resolve, { once: true });
    image.addEventListener('error', reject, { once: true });
  });

  await image.decode?.().catch(() => {});
}

/**
 * @param {HTMLImageElement} image
 */
function getImageWidths(image) {
  const srcsetWidths = Array.from(image.srcset.matchAll(/\s(\d+)w/g), (match) => Number(match[1])).filter(Boolean);

  if (srcsetWidths.length) return srcsetWidths;
  if (image.closest('.slideshow-controls__thumbnail')) return [144, 240, 352];
  if (image.closest('.dialog-thumbnails-list__thumbnail')) return [240, 352, 832, 1024];

  return DEFAULT_ENHANCED_IMAGE_WIDTHS;
}

/**
 * @param {Element} element
 * @param {string} attribute
 * @param {number} index
 */
function updateIndexedAction(element, attribute, index) {
  const value = element.getAttribute(attribute);

  if (!value) return;

  element.setAttribute(attribute, value.replace(/\/(?:open|select|handleThumbnailClick|handleThumbnailPointerEnter)\/\d+/, (match) =>
    match.replace(/\/\d+$/, `/${index}`)
  ));
}

/**
 * @template T
 * @param {Promise<T>} promise
 * @param {number} timeoutMs
 * @returns {Promise<T>}
 */
function withTimeout(promise, timeoutMs) {
  return new Promise((resolve, reject) => {
    const timeoutId = window.setTimeout(() => reject(new Error('Enhanced PDP assets request timed out')), timeoutMs);

    promise.then(resolve, reject).finally(() => window.clearTimeout(timeoutId));
  });
}

/**
 * @param {string} message
 * @param {unknown} [data]
 */
function logEnhancedPdpAssets(message, data) {
  if (data === undefined) {
    console.info(`[Enhanced PDP Assets] ${message}`);
    return;
  }

  console.info(`[Enhanced PDP Assets] ${message}`, data);
}

/**
 * @param {string} transformation
 */
function normalizeCloudinaryTransformation(transformation) {
  const normalizedTransformation = transformation.trim();

  if (!normalizedTransformation) return '';
  return normalizedTransformation.startsWith('/') ? normalizedTransformation : `/${normalizedTransformation}`;
}

/**
 * @param {string} value
 */
function stripFileExtension(value) {
  return value.replace(/\.[a-z0-9]+$/i, '');
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
